/**
 * Öffentliche Teaser-/Coming-Soon-Seite (Jun-2026 · User-Wunsch).
 * Die Plattform ist geschlossen: Fremde sehen NUR diese Seite und können
 * ausschließlich über das Kontaktformular Interesse bekunden. Bestehende
 * Nutzer melden sich über den dezenten „Login"-Button an.
 * Design: dunkle, exklusive Automotive-Ästhetik (siehe design_guidelines.json).
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, CheckCircle2, Cpu, Network, ShieldCheck, Gauge, Lock } from "lucide-react";
import api from "../lib/api";
import Logo from "../components/Logo";
import { NewsletterSignup } from "../components/NewsletterSignup";

const HERO_IMG = "https://images.unsplash.com/photo-1606927131353-c0ad17d60b56?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";
const FEATURE_ENGINE = "https://images.unsplash.com/photo-1429772011165-0c2e054367b8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const FEATURE_NIGHT = "https://images.unsplash.com/photo-1607187468260-7eb9b7c5125f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const C = { bg: "#09090B", surface: "#121214", primary: "#FF8300" };

const Field = ({ label, ...props }) => (
    <label className="block">
        <span className="text-xs font-bold uppercase tracking-[0.18em] text-zinc-400">{label}</span>
        <input
            {...props}
            className="mt-2 w-full bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-2 text-white placeholder:text-zinc-600 outline-none focus:border-[#FF4D00] transition-colors"
        />
    </label>
);

export default function PublicTeaserPage({ next = "" }) {
    const navigate = useNavigate();
    const loginUrl = next ? `/login?next=${encodeURIComponent(next)}` : "/login";
    const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", message: "", website: "" });
    const [consent, setConsent] = useState(false);
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        const id = "teaser-fonts";
        if (!document.getElementById(id)) {
            const l = document.createElement("link");
            l.id = id; l.rel = "stylesheet";
            l.href = "https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&family=IBM+Plex+Sans:wght@400;500;600&display=swap";
            document.head.appendChild(l);
        }
    }, []);

    const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            setError("Bitte Name, E-Mail und Nachricht ausfüllen."); return;
        }
        if (!consent) {
            setError("Bitte bestätigen Sie Datenschutzerklärung und AGB."); return;
        }
        setSending(true);
        try {
            await api.post("/public/contact-leads", {
                ...form,
                consent: true,
                consent_text: "Datenschutzerklärung & AGB gelesen und akzeptiert",
                consent_at: new Date().toISOString(),
            });
            setSent(true);
        } catch (err) {
            setError(err.response?.data?.detail || "Senden fehlgeschlagen. Bitte später erneut versuchen.");
        } finally {
            setSending(false);
        }
    };

    const features = [
        { icon: Cpu, title: "KI-gestützte Disposition", desc: "Automatisierte Beschaffung, Bewertung und Aufbereitung — von der Anfrage bis zur Auslieferung.", span: "md:col-span-8", img: FEATURE_ENGINE },
        { icon: Network, title: "Vernetztes Händler-Ökosystem", desc: "Hauptsitz, Filialen, Werkstatt & Vertrieb in einem System.", span: "md:col-span-4", img: FEATURE_NIGHT },
        { icon: Gauge, title: "Echtzeit-Steuerung", desc: "Bestände, Provisionen, Auslastung — live im Blick.", span: "md:col-span-4" },
        { icon: ShieldCheck, title: "Rechtssicher & DSGVO-konform", desc: "Deutsche Steuer- & Datenschutz-Standards by Design.", span: "md:col-span-8" },
    ];

    return (
        <div className="min-h-screen text-white" style={{ background: C.bg, fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }} data-testid="public-teaser-page">
            {/* Header */}
            <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#09090B]/70 border-b border-white/5">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Logo className="inline-flex items-center gap-2" boxClass="w-9 h-9" iconClass="w-5 h-5" textClass="text-xl" deClass="text-white/50" />
                    </div>
                    <button
                        onClick={() => navigate(loginUrl)}
                        className="inline-flex items-center gap-2 text-sm font-medium border border-white/15 hover:bg-white/5 px-4 py-2 rounded-none transition-colors"
                        data-testid="login-button"
                    >
                        <Lock className="w-3.5 h-3.5" /> Login
                    </button>
                </div>
            </header>

            {/* Hero */}
            <section className="relative min-h-screen flex items-center pt-16 overflow-hidden">
                <div className="absolute inset-0">
                    <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-50" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#09090B] via-[#09090B]/80 to-[#09090B]/40" />
                    <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 30% 70%, rgba(255,77,0,0.12), transparent 55%)" }} />
                </div>
                <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24">
                    <div className="max-w-3xl">
                        <p className="text-xs font-bold uppercase tracking-[0.25em] mb-6" style={{ color: C.primary }}>Bald verfügbar · Exklusiv · Geschlossene Plattform</p>
                        <h1 className="font-extrabold tracking-tighter leading-[0.95] text-4xl sm:text-6xl md:text-7xl" style={{ fontFamily: "'Outfit', sans-serif" }}>
                            Eine <span style={{ color: C.primary }}>KI-Revolution</span> für<br />
                            <span className="whitespace-nowrap text-[2rem] sm:text-5xl md:text-6xl">Motorräder & Motorroller</span>
                        </h1>
                        <p className="mt-8 text-lg text-zinc-400 leading-relaxed max-w-2xl">
                            Wir bauen die intelligenteste Plattform für den Zweirad-Handel — von der KI-gestützten Beschaffung über die Werkstatt bis zum Vertrieb. Der Zugang ist exklusiv und nur für registrierte Partner.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <a href="#kontakt" className="inline-flex items-center gap-2 px-7 py-3.5 font-bold uppercase tracking-widest text-sm rounded-none transition-transform hover:-translate-y-0.5" style={{ background: C.primary }} data-testid="cta-contact">
                                Interesse bekunden <ArrowRight className="w-4 h-4" />
                            </a>
                            <button onClick={() => navigate(loginUrl)} className="inline-flex items-center gap-2 px-7 py-3.5 font-bold uppercase tracking-widest text-sm border border-white/15 hover:bg-white/5 rounded-none transition-colors">
                                Partner-Login
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Feature-Teaser */}
            <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 sm:py-32">
                <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3" style={{ color: C.primary }}>Was entsteht</p>
                <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-12" style={{ fontFamily: "'Outfit', sans-serif" }}>Ein System. Der ganze Zweirad-Handel.</h2>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
                    {features.map((f, i) => {
                        const Icon = f.icon;
                        return (
                            <div key={i} className={`${f.span} relative overflow-hidden border border-white/5 hover:border-white/20 transition-colors p-8 min-h-[200px] flex flex-col justify-end`} style={{ background: C.surface }} data-testid={`teaser-feature-${i}`}>
                                {f.img && (
                                    <div className="absolute inset-0 opacity-20">
                                        <img src={f.img} alt="" className="w-full h-full object-cover" />
                                        <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${C.surface}, transparent)` }} />
                                    </div>
                                )}
                                <div className="relative">
                                    <Icon className="w-6 h-6 mb-4 text-zinc-300" strokeWidth={1.5} />
                                    <h3 className="text-xl font-medium mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{f.title}</h3>
                                    <p className="text-sm text-zinc-400 leading-relaxed">{f.desc}</p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Kontakt */}
            <section id="kontakt" className="max-w-7xl mx-auto px-6 lg:px-12 pb-24 sm:pb-32">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                    <div>
                        <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3" style={{ color: C.primary }}>Kontakt</p>
                        <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>Werden Sie Partner der ersten Stunde.</h2>
                        <p className="text-zinc-400 leading-relaxed">
                            Der Zugang ist aktuell nur auf Einladung möglich. Hinterlassen Sie uns Ihre Daten — wir melden uns persönlich, sobald wir Plätze für neue Partner öffnen.
                        </p>
                    </div>
                    <div className="border border-white/10 p-8 sm:p-10" style={{ background: C.surface }}>
                        {sent ? (
                            <div className="flex flex-col items-center text-center py-10" data-testid="contact-success-message">
                                <CheckCircle2 className="w-12 h-12 mb-4" style={{ color: "#10B981" }} />
                                <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Vielen Dank.</h3>
                                <p className="text-zinc-400">Wir haben Ihre Anfrage erhalten und melden uns in Kürze.</p>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
                                {/* Honeypot (für Bots, visuell versteckt) */}
                                <input type="text" value={form.website} onChange={upd("website")} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
                                <div className="grid sm:grid-cols-2 gap-6">
                                    <Field label="Name *" value={form.name} onChange={upd("name")} placeholder="Max Mustermann" data-testid="contact-name-input" />
                                    <Field label="Firma" value={form.company} onChange={upd("company")} placeholder="Muster GmbH" data-testid="contact-company-input" />
                                </div>
                                <div className="grid sm:grid-cols-2 gap-6">
                                    <Field label="E-Mail *" type="email" value={form.email} onChange={upd("email")} placeholder="max@firma.de" data-testid="contact-email-input" />
                                    <Field label="Telefon" value={form.phone} onChange={upd("phone")} placeholder="+49 …" data-testid="contact-phone-input" />
                                </div>
                                <label className="block">
                                    <span className="text-xs font-bold uppercase tracking-[0.18em] text-zinc-400">Nachricht *</span>
                                    <textarea
                                        value={form.message} onChange={upd("message")} rows={4}
                                        placeholder="Erzählen Sie uns kurz von Ihrem Betrieb…"
                                        className="mt-2 w-full bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-2 text-white placeholder:text-zinc-600 outline-none focus:border-[#FF4D00] transition-colors resize-none"
                                        data-testid="contact-message-input"
                                    />
                                </label>
                                {error && <p className="text-sm" style={{ color: "#EF4444" }} data-testid="contact-error">{error}</p>}
                                <label className="flex items-start gap-2.5 cursor-pointer select-none" data-testid="contact-consent-label">
                                    <input
                                        type="checkbox"
                                        checked={consent}
                                        onChange={(e) => setConsent(e.target.checked)}
                                        className="mt-0.5 w-4 h-4 shrink-0 accent-[#FF4D00] cursor-pointer"
                                        data-testid="contact-consent-checkbox"
                                        aria-label="Datenschutzerklärung und AGB akzeptieren"
                                    />
                                    <span className="text-[11px] leading-relaxed text-zinc-400">
                                        Ich habe die <a href="/legal/datenschutz" target="_blank" rel="noopener noreferrer" className="underline hover:text-zinc-200" data-testid="contact-consent-datenschutz-link">Datenschutzerklärung</a> und
                                        die <a href="/legal/agb" target="_blank" rel="noopener noreferrer" className="underline hover:text-zinc-200" data-testid="contact-consent-agb-link">AGB</a> gelesen
                                        und stimme der Verarbeitung meiner Angaben zur Kontaktaufnahme zu. *
                                    </span>
                                </label>
                                <button
                                    type="submit" disabled={sending || !consent}
                                    className="w-full py-3.5 font-bold uppercase tracking-widest text-sm rounded-none transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                    style={{ background: C.primary, color: "#09090B" }}
                                    data-testid="submit-contact-form"
                                >
                                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                                    {sending ? "Wird gesendet…" : "Kontakt aufnehmen"}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </section>

            {/* Newsletter-Anmeldung (Jul-2026, User-Wunsch) */}
            <div className="max-w-7xl mx-auto px-2 lg:px-6 pb-12">
                <NewsletterSignup />
            </div>

            {/* Footer */}
            <footer className="border-t border-white/5">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-zinc-400">
                    <Logo className="inline-flex items-center gap-2 text-zinc-300" boxClass="w-8 h-8" iconClass="w-4 h-4" textClass="text-lg" deClass="text-zinc-500" />
                    <div className="flex items-center gap-6">
                        <a href="/impressum" className="hover:text-zinc-200 transition-colors" data-testid="footer-impressum">Impressum</a>
                        <a href="/datenschutz" className="hover:text-zinc-200 transition-colors" data-testid="footer-datenschutz">Datenschutz</a>
                    </div>
                    <span>© {new Date().getFullYear()} TwoWheels</span>
                </div>
            </footer>
        </div>
    );
}
